import { type ReactNode } from "react";

export enum PreviewType {
  Image = "IMAGE",
  Video = "VIDEO",
}

export type Article = {
  id: string;
  slug: string;
  title: string;
  description: string;
  previewUrl?: string;
  previewType?: PreviewType;
  pdfUrl?: string;
  author: string;
  publishedDate: string;
  lastModified?: string;
  readingTime: number;
  sections: ArticleSection[];
  published: boolean;
  source?: {
    label: string;
    url: string;
  };
};

export enum ArticleSectionType {
  TITLE = "title",
  TEXT = "text",
  IMAGE = "image",
  TABLE = "table",
  CHART = "chart",
  CASH_FLOW_PROFILES = "cash-flow-profiles",
  SCATTER_CHART = "scatter-chart",
  CARD_GRID = "card-grid",
  SCENARIO_RETURNS = "scenario-returns",
}

export type ArticleSection =
  | ArticleCardGridSection
  | ArticleCashFlowProfilesSection
  | ArticleChartSection
  | ArticleImageSection
  | ArticleScatterChartSection
  | ArticleScenarioReturnsSection
  | ArticleTableSection
  | ArticleTextSection
  | ArticleTitleSection;

export type ArticleTitleSection = {
  id: string;
  type: ArticleSectionType.TITLE;
  content: {
    text: string;
    level: 1 | 2 | 3 | 4 | 5 | 6;
    className?: string;
  };
};

export type ArticleTextSection = {
  id: string;
  type: ArticleSectionType.TEXT;
  content: {
    html: string;
  };
};

export type ArticleCardGridSection = {
  id: string;
  type: ArticleSectionType.CARD_GRID;
  content: {
    title?: string;
    items: string[];
  };
};

export type ArticleScenarioReturnsSection = {
  id: string;
  type: ArticleSectionType.SCENARIO_RETURNS;
  content: {
    title: string;
    yAxisTicks?: number[];
    labels: {
      assumptionsTitle: string;
      calculationsTitle: string;
      equityValueLabel: string;
      irrLabel: string;
      moicLabel: string;
      equityLabel: string;
      debtLabel: string;
    };
    scenarios: {
      id: string;
      title: string;
      bars: {
        label: string;
        debt: number;
        equity: number;
      }[];
      annotations?: {
        value: number;
        label: string;
      }[];
      assumptions: {
        label: string;
        value: string;
      }[];
      calculations: {
        label: string;
        value?: string;
        indent?: boolean;
        emphasis?: boolean;
      }[];
      summary: {
        irr: string;
        moic: string;
      };
    }[];
  };
};

export type ArticleImageSection = {
  id: string;
  type: ArticleSectionType.IMAGE;
  content: {
    url: string;
    alt?: string;
    caption?: string;
  };
};

export type TableCellContent =
  | string
  | {
      type: "bullets";
      items: string[];
    }
  | {
      type: "text";
      content: string;
    };

export type ArticleTableSection = {
  id: string;
  type: ArticleSectionType.TABLE;
  content: {
    headers: string[];
    rows: TableCellContent[][];
  };
};

export type ArticleChartSection = {
  id: string;
  type: ArticleSectionType.CHART;
  content: {
    title?: string;
    icon?: ReactNode;
    data: {
      [key: string]: number | string | null | undefined;
      category: string;
    }[];
    metrics: Record<
      string,
      {
        label: string;
        color: string;
        type: "bar" | "line";
        yAxisId: "left" | "right";
        formatter?: (value: number) => string;
        axisLabel?: string;
      }
    >;
    footerText?: string;
    height?: "large" | "medium" | "small";
  };
};

export type ArticleCashFlowProfilesSection = {
  id: string;
  type: ArticleSectionType.CASH_FLOW_PROFILES;
  content: {
    title?: string;
    legend?: {
      contributionsLabel: string;
      distributionsLabel: string;
    };
    profiles: {
      id: string;
      title?: string;
      showYAxis?: boolean;
      yAxisTicks?: number[];
      data: {
        year: string;
        contributions: number;
        distributions: number;
      }[];
    }[];
  };
};

export type ScatterChartDataPoint = {
  id: string;
  x: number;
  y: number;
  label?: string[] | string;
  showLabel?: boolean;
  labelPosition?: "bottom" | "left" | "right" | "top";
  labelStyle?: "bullets" | "text";
};

export type ScatterChartArrow = {
  from: string;
  to: string;
  color?: string;
  strokeWidth?: number;
  dashed?: boolean;
};

export type ArticleScatterChartSection = {
  id: string;
  type: ArticleSectionType.SCATTER_CHART;
  content: {
    title?: string;
    icon?: ReactNode;
    data: ScatterChartDataPoint[];
    arrows?: ScatterChartArrow[];
    xAxisLabel?: string;
    xAxisUnit?: string;
    yAxisUnit?: string;
    xDomain?: [number, number];
    yDomain?: [number, number];
    markerColor?: string;
    markerBorderColor?: string;
    labelColor?: string;
    labelBulletColor?: string;
    arrowColor?: string;
    height?: number;
    footerText?: string;
  };
};
